import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import {
  LayoutDashboard,
  CheckCircle,
  Users,
  Image as ImageIcon,
  Eye,
  LogOut,
  Plus,
  Trash2,
  ExternalLink,
  Send,
} from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("reservations");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user, tab]);

  if (user === undefined) return null;
  if (user === null) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div
      data-testid={ADMIN.layout}
      className="min-h-screen flex bg-[var(--ivory)]"
    >
      {/* Sidebar */}
      <aside className="w-64 shrink-0 jewel-bg text-ivory p-6 hidden md:flex flex-col" style={{ color: "var(--ivory)" }}>
        <div className="flex items-center justify-center">
          <Logo size={96} />
        </div>

        <nav className="mt-10 space-y-1">
          <SideItem
            testid={ADMIN.navReservations}
            active={tab === "reservations"}
            onClick={() => setTab("reservations")}
            icon={CheckCircle}
            label="Reservations"
          />
          <SideItem
            testid={ADMIN.navVisitors}
            active={tab === "visitors"}
            onClick={() => setTab("visitors")}
            icon={LayoutDashboard}
            label="Visitor Log"
          />
          <SideItem
            testid={ADMIN.navSignups}
            active={tab === "signups"}
            onClick={() => setTab("signups")}
            icon={Users}
            label="Email Signups"
          />
          <SideItem
            testid={ADMIN.navArtworks}
            active={tab === "artworks"}
            onClick={() => setTab("artworks")}
            icon={ImageIcon}
            label="Artworks"
          />
          <SideItem
            testid="admin-nav-campaigns"
            active={tab === "campaigns"}
            onClick={() => setTab("campaigns")}
            icon={Send}
            label="Campaigns"
          />
        </nav>

        <div className="mt-auto pt-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ivory/50">
            Signed in as
          </div>
          <div className="text-sm mt-1 truncate">{user.email}</div>
          <button
            data-testid={ADMIN.logoutBtn}
            onClick={handleLogout}
            className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#D4AF37] hover:opacity-80"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <header className="border-b border-black/5 px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#722F37]">
              National Coordinator&rsquo;s Console
            </div>
            <h1 className="font-serif text-3xl mt-1 tracking-tight capitalize">
              {tab.replace("_", " ")}
            </h1>
          </div>
        </header>

        {stats && (
          <StatsRow stats={stats} />
        )}

        <div className="px-6 md:px-10 pb-16">
          {tab === "reservations" && <ReservationsPanel />}
          {tab === "visitors" && <VisitorsPanel />}
          {tab === "signups" && <SignupsPanel />}
          {tab === "artworks" && <ArtworksPanel />}
          {tab === "campaigns" && <CampaignsPanel />}
        </div>
      </main>
    </div>
  );
}

function SideItem({ testid, active, onClick, icon: Icon, label }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm tracking-wide transition-colors ${
        active
          ? "text-[#D4AF37] bg-white/5 border-l-2 border-[#D4AF37]"
          : "text-ivory/70 hover:text-ivory border-l-2 border-transparent"
      }`}
    >
      <Icon size={16} strokeWidth={1.5} />
      <span>{label}</span>
    </button>
  );
}

function StatsRow({ stats }) {
  const cards = [
    { k: "Visitors", v: stats.total_visitors, tid: "admin-stat-visitors" },
    { k: "Page views", v: stats.total_page_views, tid: "admin-stat-pageviews" },
    { k: "Reservations", v: stats.total_reservations, sub: `${stats.pending_reservations} pending`, tid: "admin-stat-reservations" },
    { k: "Signups", v: stats.total_signups, tid: "admin-stat-signups" },
    { k: "Artworks", v: stats.total_artworks, tid: "admin-stat-artworks" },
    {
      k: "Pending revenue",
      v: `$${stats.revenue_pending.toLocaleString()}`,
      tid: "admin-stat-revenue-pending",
    },
  ];
  return (
    <div className="px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-6 gap-4 border-b border-black/5">
      {cards.map((c) => (
        <div key={c.k} data-testid={c.tid} className="border border-black/10 bg-white p-4">
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/50">
            {c.k}
          </div>
          <div className="font-serif text-2xl mt-1 text-[#722F37]">{c.v}</div>
          {c.sub && (
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#1A1A1A]/50 mt-1">
              {c.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* --- Panels --- */
function badgeColor(status) {
  switch (status) {
    case "pending":
      return "bg-[#F1E6C6] text-[#7A5B10]";
    case "approved":
    case "escrow_created":
      return "bg-[#E0EDE4] text-[#0B3B24]";
    case "funded":
      return "bg-[#DFF1E5] text-[#0B3B24]";
    case "completed":
      return "bg-[#0B3B24] text-[#F9F6F0]";
    case "cancelled":
      return "bg-[#F3D5D8] text-[#722F37]";
    default:
      return "bg-black/10 text-[#1A1A1A]";
  }
}

function ReservationsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/reservations")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const approve = async (id) => {
    try {
      await api.post(`/admin/reservations/${id}/approve`);
      toast.success("Reservation approved. Escrow link generated.");
      load();
    } catch (e) {
      toast.error("Approval failed.");
    }
  };
  const cancel = async (id) => {
    try {
      await api.post(`/admin/reservations/${id}/cancel`);
      toast.success("Reservation cancelled.");
      load();
    } catch (e) {
      toast.error("Cancel failed.");
    }
  };

  if (loading) return <div className="py-10 text-sm text-[#1A1A1A]/60">Loading…</div>;
  if (rows.length === 0)
    return (
      <div className="py-16 text-center text-sm text-[#1A1A1A]/50 font-serif italic">
        No reservations yet — they will appear here as buyers submit the form.
      </div>
    );

  return (
    <div className="mt-6 border border-black/10 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/60">
          <tr className="border-b border-black/10">
            <th className="text-left px-4 py-3">Buyer</th>
            <th className="text-left px-4 py-3">Artwork</th>
            <th className="text-left px-4 py-3">Price</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Escrow</th>
            <th className="text-left px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={ADMIN.reservationRow(r.id)}
              className="border-b border-black/5 last:border-0"
            >
              <td className="px-4 py-4 align-top">
                <div className="font-medium">{r.buyer_name}</div>
                <div className="text-xs text-[#1A1A1A]/60">{r.buyer_email}</div>
                {r.buyer_phone && (
                  <div className="text-xs text-[#1A1A1A]/50 mt-0.5">{r.buyer_phone}</div>
                )}
              </td>
              <td className="px-4 py-4 align-top font-serif">{r.artwork_title}</td>
              <td className="px-4 py-4 align-top">${Number(r.artwork_price).toLocaleString()}</td>
              <td className="px-4 py-4 align-top">
                <span
                  className={`inline-block px-2.5 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase ${badgeColor(r.status)}`}
                >
                  {r.status.replace("_", " ")}
                </span>
              </td>
              <td className="px-4 py-4 align-top">
                {r.escrow_pay_url ? (
                  <a
                    href={r.escrow_pay_url}
                    data-testid={ADMIN.payLink(r.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[#722F37] hover:text-[#0B3B24] text-xs"
                  >
                    Pay link <ExternalLink size={12} strokeWidth={1.5} />
                  </a>
                ) : (
                  <span className="text-xs text-[#1A1A1A]/40">—</span>
                )}
                {r.escrow_transaction_id && (
                  <div className="text-[10px] text-[#1A1A1A]/40 mt-1">
                    {r.escrow_transaction_id}
                  </div>
                )}
                {r.admin_notes && (
                  <div
                    className={`text-[10px] mt-1 max-w-[220px] ${
                      r.admin_notes.startsWith("Live")
                        ? "text-[#0B3B24]"
                        : "text-[#722F37]"
                    }`}
                    title={r.admin_notes}
                  >
                    {r.admin_notes.startsWith("Live") ? "● Live" : "● Placeholder"}
                  </div>
                )}
              </td>
              <td className="px-4 py-4 align-top">
                <div className="flex flex-col gap-2">
                  {r.status === "pending" && (
                    <button
                      data-testid={ADMIN.approveBtn(r.id)}
                      onClick={() => approve(r.id)}
                      className="btn-gold text-[10px] tracking-[0.3em] uppercase h-8 px-3"
                    >
                      Approve
                    </button>
                  )}
                  {r.status !== "cancelled" && r.status !== "completed" && (
                    <button
                      data-testid={ADMIN.cancelBtn(r.id)}
                      onClick={() => cancel(r.id)}
                      className="text-[10px] tracking-[0.3em] uppercase h-8 px-3 border border-[#722F37]/50 text-[#722F37] hover:bg-[#722F37] hover:text-ivory transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <div className="pt-1 flex flex-col gap-1 border-t border-black/5 mt-1">
                    <a
                      href={`${API_BASE}/preview/certificate.pdf?reservation_id=${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-cert-download-${r.id}`}
                      className="text-[10px] tracking-[0.28em] uppercase text-[#0B3B24] hover:text-[#D4AF37] transition-colors"
                    >
                      Certificate PDF ↓
                    </a>
                    <a
                      href={`${API_BASE}/preview/letter.pdf?reservation_id=${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-letter-download-${r.id}`}
                      className="text-[10px] tracking-[0.28em] uppercase text-[#0B3B24] hover:text-[#D4AF37] transition-colors"
                    >
                      Letter PDF ↓
                    </a>
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VisitorsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/admin/visitor-events?limit=500")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 text-sm text-[#1A1A1A]/60">Loading…</div>;
  if (rows.length === 0)
    return (
      <div className="py-16 text-center text-sm text-[#1A1A1A]/50 font-serif italic">
        No visitor events tracked yet.
      </div>
    );
  return (
    <div className="mt-6 border border-black/10 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/60">
          <tr className="border-b border-black/10">
            <th className="text-left px-4 py-3">When</th>
            <th className="text-left px-4 py-3">Event</th>
            <th className="text-left px-4 py-3">Path</th>
            <th className="text-left px-4 py-3">Device</th>
            <th className="text-left px-4 py-3">Session</th>
            <th className="text-left px-4 py-3">Referrer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-black/5 last:border-0">
              <td className="px-4 py-3 text-xs text-[#1A1A1A]/70">
                {new Date(r.created_at).toLocaleString()}
              </td>
              <td className="px-4 py-3">
                <span className="inline-block px-2 py-0.5 bg-[#F1E6C6] text-[#7A5B10] rounded-full text-[10px] uppercase tracking-[0.15em]">
                  {r.event_type}
                </span>
              </td>
              <td className="px-4 py-3 text-xs">{r.path || "—"}</td>
              <td className="px-4 py-3 text-xs">{r.device || "—"}</td>
              <td className="px-4 py-3 text-xs text-[#1A1A1A]/50 truncate max-w-[140px]">
                {r.session_id || "—"}
              </td>
              <td className="px-4 py-3 text-xs text-[#1A1A1A]/60 truncate max-w-[220px]">
                {r.referrer || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SignupsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get("/admin/email-signups")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-10 text-sm text-[#1A1A1A]/60">Loading…</div>;
  if (rows.length === 0)
    return (
      <div className="py-16 text-center text-sm text-[#1A1A1A]/50 font-serif italic">
        No email signups yet.
      </div>
    );
  return (
    <div className="mt-6 border border-black/10 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/60">
          <tr className="border-b border-black/10">
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Source</th>
            <th className="text-left px-4 py-3">When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-black/5 last:border-0">
              <td className="px-4 py-3">{r.email}</td>
              <td className="px-4 py-3 text-xs uppercase tracking-[0.25em] text-[#722F37]">
                {r.source}
              </td>
              <td className="px-4 py-3 text-xs text-[#1A1A1A]/60">
                {new Date(r.created_at).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArtworksPanel() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    price: 0,
    image_url: "",
    medium: "Oil on canvas",
    dimensions: "",
    year: new Date().getFullYear(),
    is_available: true,
    display_order: 100,
  });
  const [editingId, setEditingId] = useState(null);

  const load = () =>
    api.get("/artworks").then((r) => setRows(r.data || []));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({
      title: "",
      subtitle: "",
      description: "",
      price: 0,
      image_url: "",
      medium: "Oil on canvas",
      dimensions: "",
      year: new Date().getFullYear(),
      is_available: true,
      display_order: 100,
    });
    setShowForm(true);
  };

  const openEdit = (art) => {
    setEditingId(art.id);
    setForm({
      title: art.title || "",
      subtitle: art.subtitle || "",
      description: art.description || "",
      price: art.price || 0,
      image_url: art.image_url || "",
      medium: art.medium || "",
      dimensions: art.dimensions || "",
      year: art.year || new Date().getFullYear(),
      is_available: art.is_available !== false,
      display_order: art.display_order || 0,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price), year: Number(form.year), display_order: Number(form.display_order) };
      if (editingId) {
        await api.put(`/admin/artworks/${editingId}`, payload);
        toast.success("Artwork updated.");
      } else {
        await api.post("/admin/artworks", payload);
        toast.success("Artwork added.");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error("Save failed.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this artwork from the gallery?")) return;
    try {
      await api.delete(`/admin/artworks/${id}`);
      toast.success("Removed.");
      load();
    } catch (_) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <button
          data-testid={ADMIN.addArtworkBtn}
          onClick={openNew}
          className="btn-gold h-10 px-5 inline-flex items-center gap-2 tracking-[0.25em] text-[10px] uppercase"
        >
          <Plus size={14} strokeWidth={1.5} /> New artwork
        </button>
      </div>
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[9px] tracking-[0.3em] uppercase text-[#1A1A1A]/60">
            <tr className="border-b border-black/10">
              <th className="text-left px-4 py-3">Image</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Price</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr
                key={a.id}
                data-testid={ADMIN.artworkRow(a.id)}
                className="border-b border-black/5 last:border-0"
              >
                <td className="px-4 py-3">
                  <img
                    src={a.image_url}
                    alt={a.title}
                    className="w-16 h-20 object-cover"
                  />
                </td>
                <td className="px-4 py-3 font-serif">{a.title}</td>
                <td className="px-4 py-3">${Number(a.price).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] rounded-full ${
                      a.is_available
                        ? "bg-[#E0EDE4] text-[#0B3B24]"
                        : "bg-[#F3D5D8] text-[#722F37]"
                    }`}
                  >
                    {a.is_available ? "Available" : "Reserved"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(a)}
                      className="text-[10px] tracking-[0.25em] uppercase h-8 px-3 border border-[#0B3B24]/40 text-[#0B3B24] hover:bg-[#0B3B24] hover:text-ivory transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      data-testid={ADMIN.deleteArtworkBtn(a.id)}
                      onClick={() => del(a.id)}
                      className="text-[10px] tracking-[0.25em] uppercase h-8 px-3 border border-[#722F37]/40 text-[#722F37] hover:bg-[#722F37] hover:text-ivory transition-colors inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} strokeWidth={1.5} /> Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-6"
          style={{ background: "rgba(6,10,8,0.6)", backdropFilter: "blur(12px)" }}
          onClick={() => setShowForm(false)}
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg p-8 space-y-4 border border-[#D4AF37]"
          >
            <h3 className="font-serif text-2xl">
              {editingId ? "Edit artwork" : "Add artwork"}
            </h3>
            <FormField label="Title">
              <input
                required
                data-testid={ADMIN.artworkFormTitle}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
              />
            </FormField>
            <FormField label="Subtitle">
              <input
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
              />
            </FormField>
            <FormField label="Description">
              <textarea
                data-testid={ADMIN.artworkFormDescription}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37] resize-none"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Price (USD)">
                <input
                  data-testid={ADMIN.artworkFormPrice}
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
                  required
                />
              </FormField>
              <FormField label="Year">
                <input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
                />
              </FormField>
              <FormField label="Medium">
                <input
                  value={form.medium}
                  onChange={(e) => setForm({ ...form, medium: e.target.value })}
                  className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
                />
              </FormField>
              <FormField label="Dimensions">
                <input
                  value={form.dimensions}
                  onChange={(e) => setForm({ ...form, dimensions: e.target.value })}
                  className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
                />
              </FormField>
            </div>
            <FormField label="Image URL">
              <input
                data-testid={ADMIN.artworkFormImage}
                required
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="w-full border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
              />
            </FormField>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_available}
                onChange={(e) => setForm({ ...form, is_available: e.target.checked })}
              />
              Available for reservation
            </label>
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="h-10 px-5 text-[10px] tracking-[0.25em] uppercase border border-black/20"
              >
                Cancel
              </button>
              <button
                data-testid={ADMIN.artworkFormSubmit}
                type="submit"
                className="btn-gold h-10 px-6 text-[10px] tracking-[0.25em] uppercase"
              >
                {editingId ? "Save" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase text-[#1A1A1A]/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function CampaignsPanel() {
  const [history, setHistory] = useState([]);
  const [signupCounts, setSignupCounts] = useState({ all: 0, splash: 0, landing: 0, footer: 0, buyers: 0 });
  const [subject, setSubject] = useState("");
  const [template, setTemplate] = useState("house_note");

  const TEMPLATES = {
    house_note: {
      label: "House note (elegant)",
      subject: "A quiet note from the House",
      body: `<div style="max-width:600px; margin:0 auto; font-family: Georgia, 'Times New Roman', serif; background:#F9F6F0; color:#1A1A1A;">
  <div style="background:#0B3B24; padding:36px 32px; text-align:center;">
    <div style="font-size:10px; letter-spacing:0.5em; text-transform:uppercase; color:#D4AF37;">House of</div>
    <div style="font-family:Georgia, serif; font-size:32px; color:#F9F6F0; margin-top:6px; letter-spacing:-0.01em;">Master Brushes</div>
    <div style="font-style:italic; font-size:13px; color:#D4AF37; opacity:0.9; margin-top:4px;">Fine Art</div>
  </div>
  <div style="height:2px; background:linear-gradient(to right, transparent, #D4AF37, transparent);"></div>
  <div style="padding:36px 32px;">
    <div style="font-size:10px; letter-spacing:0.4em; text-transform:uppercase; color:#722F37;">A note from the House</div>
    <h1 style="font-family:Georgia, serif; font-weight:400; font-size:28px; color:#0B3B24; margin:8px 0 20px 0; letter-spacing:-0.01em;">[Your headline here]</h1>
    <p style="font-size:15px; line-height:1.7; color:#1A1A1A;">Dear friend of the House,</p>
    <p style="font-size:15px; line-height:1.7; color:#1A1A1A;">[Write the body of your message here.]</p>
    <div style="text-align:center; margin:32px 0;">
      <a href="https://brushmaster.art" style="display:inline-block; background:#D4AF37; color:#1A1A1A; padding:14px 32px; text-decoration:none; letter-spacing:0.3em; text-transform:uppercase; font-size:11px; font-weight:600; font-family:Georgia, serif;">Enter the House →</a>
    </div>
    <p style="font-size:15px; line-height:1.7; color:#1A1A1A;">With gratitude,</p>
    <p style="font-family:Georgia, serif; font-size:16px; color:#722F37; margin:4px 0 0 0;">Edgar Hicks<span style="display:block; font-style:italic; font-size:12px; color:#1A1A1A; opacity:0.7;">National Coordinator, House of Master Brushes Fine Art</span></p>
  </div>
  <div style="background:#EFE7D6; padding:22px 32px; text-align:center; font-size:10px; letter-spacing:0.35em; text-transform:uppercase; color:#722F37;">Excellence in art &nbsp;·&nbsp; Legacy in collection</div>
  <div style="background:#0B3B24; padding:16px 32px; text-align:center; font-size:10px; letter-spacing:0.3em; text-transform:uppercase; color:rgba(249,246,240,0.55);">brushmaster.art &nbsp;·&nbsp; Payments secured by Escrow.com</div>
</div>`,
    },
    gala_invitation: {
      label: "Gala invitation (cold-email CTA)",
      subject: "America turned 250. The vault opens.",
      body: `<div style="max-width:620px; margin:0 auto; font-family: Georgia, 'Times New Roman', serif; background:#0B3B24; color:#F9F6F0;">
  <div style="padding:44px 32px 20px 32px; text-align:center;">
    <div style="font-size:10px; letter-spacing:0.55em; text-transform:uppercase; color:#D4AF37;">House of Master Brushes Fine Art</div>
    <div style="height:1px; width:120px; margin:18px auto; background:linear-gradient(to right, transparent, #D4AF37, transparent);"></div>
    <h1 style="font-family:Georgia, serif; font-weight:300; font-size:36px; line-height:1.1; color:#F9F6F0; margin:0; letter-spacing:-0.01em;">America turned <em style="color:#D4AF37; font-style:italic;">250</em>.<br/>The vault opens.</h1>
    <p style="font-size:14px; letter-spacing:0.15em; text-transform:uppercase; color:#D4AF37; opacity:0.85; margin-top:20px;">A private invitation</p>
  </div>

  <div style="padding:8px 40px 28px 40px;">
    <p style="font-size:16px; line-height:1.75; color:rgba(249,246,240,0.92);">Dear collector,</p>
    <p style="font-size:16px; line-height:1.75; color:rgba(249,246,240,0.92);">
      To mark the two-hundred-fiftieth year of the Republic, the House of Master Brushes has opened a <em>private vault</em> of Tom Pergola&rsquo;s original works &mdash; 205 pieces spanning his celebrated <em>assemblage plastique</em>, celebrity commissions, and fine-art paintings.
    </p>
    <p style="font-size:16px; line-height:1.75; color:rgba(249,246,240,0.92);">
      Every acquired piece carries a signed Certificate of Authenticity, a Letter of Appreciation, and secured escrow through Escrow.com. And this year, one thing more:
    </p>
    <p style="font-size:17px; line-height:1.7; color:#D4AF37; font-style:italic; text-align:center; padding:14px 20px; border-top:1px solid rgba(212,175,55,0.4); border-bottom:1px solid rgba(212,175,55,0.4); margin:26px 0;">
      Only patrons who acquire before the gala will receive a personal invitation.
    </p>
  </div>

  <div style="text-align:center; padding:0 32px 8px 32px;">
    <a href="https://brushmaster.art" style="display:inline-block; background:#D4AF37; color:#0B3B24; padding:16px 38px; text-decoration:none; letter-spacing:0.28em; text-transform:uppercase; font-size:12px; font-weight:700; font-family:Georgia, serif;">Enter the vault &rarr;</a>
    <div style="font-size:11px; letter-spacing:0.35em; text-transform:uppercase; color:rgba(249,246,240,0.55); margin-top:14px;">brushmaster.art</div>
  </div>

  <div style="padding:32px 40px 12px 40px; text-align:center;">
    <div style="font-size:11px; letter-spacing:0.3em; text-transform:uppercase; color:rgba(249,246,240,0.6);">Gala evening</div>
    <div style="font-family:Georgia, serif; font-size:22px; color:#F9F6F0; margin-top:6px;">Date &amp; venue &mdash; to be announced</div>
    <div style="font-size:11px; letter-spacing:0.25em; text-transform:uppercase; color:rgba(212,175,55,0.75); margin-top:6px;">By private invitation only</div>
  </div>

  <div style="padding:20px 40px 32px 40px;">
    <p style="font-size:15px; line-height:1.7; color:rgba(249,246,240,0.9);">With regards,</p>
    <p style="font-family:Georgia, serif; font-size:18px; color:#D4AF37; margin:2px 0 0 0;">Edgar Hicks<span style="display:block; font-style:italic; font-size:12px; color:rgba(249,246,240,0.65); margin-top:2px;">National Coordinator, House of Master Brushes Fine Art</span></p>
  </div>

  <div style="background:#062818; padding:20px 40px; text-align:center; font-size:10px; letter-spacing:0.35em; text-transform:uppercase; color:rgba(212,175,55,0.7);">
    Excellence in art &nbsp;·&nbsp; Legacy in collection
  </div>
  <div style="background:#030f09; padding:16px 40px; text-align:center; font-size:10px; letter-spacing:0.3em; text-transform:uppercase; color:rgba(249,246,240,0.4);">
    brushmaster.art &nbsp;·&nbsp; Payments secured by Escrow.com
  </div>
</div>`,
    },
  };

  const [body, setBody] = useState(TEMPLATES.house_note.body);
  const [segment, setSegment] = useState("all_signups");
  const [sending, setSending] = useState(false);

  const applyTemplate = (key) => {
    setTemplate(key);
    setSubject(TEMPLATES[key].subject);
    setBody(TEMPLATES[key].body);
  };

  const loadCounts = async () => {
    try {
      const [signups, buyers, campaigns] = await Promise.all([
        api.get("/admin/email-signups"),
        api.get("/admin/reservations"),
        api.get("/admin/campaigns"),
      ]);
      const rows = signups.data || [];
      setSignupCounts({
        all: rows.length,
        splash: rows.filter((r) => r.source === "splash").length,
        landing: rows.filter((r) => r.source === "landing").length,
        footer: rows.filter((r) => r.source === "footer").length,
        buyers: new Set((buyers.data || []).map((r) => r.buyer_email)).size,
      });
      setHistory(campaigns.data || []);
    } catch (_) {
      /* ignore */
    }
  };
  useEffect(() => {
    loadCounts();
  }, []);

  const recipientCount =
    segment === "all_signups" ? signupCounts.all :
    segment === "splash" ? signupCounts.splash :
    segment === "landing" ? signupCounts.landing :
    segment === "footer" ? signupCounts.footer :
    segment === "all_buyers" ? signupCounts.buyers : 0;

  const send = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    if (!window.confirm(`Send "${subject}" to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns", {
        subject: subject,
        html_body: body,
        segment: segment,
      });
      toast.success(`Sent: ${data.delivered} delivered · ${data.failed} failed`);
      loadCounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-8" data-testid="admin-campaigns-panel">
      <form onSubmit={send} className="bg-white border border-black/10 p-6 space-y-5">
        <div>
          <h3 className="font-serif text-2xl">Compose broadcast</h3>
          <p className="text-xs text-[#1A1A1A]/60 mt-1">
            Sends via Resend from edhix26@brushmaster.art with your name in reply-to.
          </p>
        </div>

        <FormField label="Template">
          <div className="flex gap-2 flex-wrap" data-testid="campaign-template-picker">
            {Object.entries(TEMPLATES).map(([key, t]) => (
              <button
                key={key}
                type="button"
                data-testid={`campaign-template-${key}`}
                onClick={() => applyTemplate(key)}
                className={`text-[10px] tracking-[0.25em] uppercase px-3 py-2 border transition ${
                  template === key
                    ? "bg-[#0B3B24] text-[#F9F6F0] border-[#0B3B24]"
                    : "bg-transparent text-[#1A1A1A] border-black/20 hover:border-[#D4AF37] hover:text-[#0B3B24]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </FormField>

        <FormField label="Recipient segment">
          <select
            data-testid="campaign-segment"
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            className="w-full bg-transparent border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
          >
            <option value="all_signups">All email signups ({signupCounts.all})</option>
            <option value="splash">Coming-Soon splash signups ({signupCounts.splash})</option>
            <option value="landing">Landing hero signups ({signupCounts.landing})</option>
            <option value="footer">Footer newsletter signups ({signupCounts.footer})</option>
            <option value="all_buyers">All reservation buyers ({signupCounts.buyers})</option>
          </select>
        </FormField>

        <FormField label="Subject">
          <input
            required
            data-testid="campaign-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full bg-transparent border-b border-black/20 py-1 outline-none focus:border-[#D4AF37]"
            placeholder="e.g. Gala week — final invitations open"
          />
        </FormField>

        <FormField label="HTML body">
          <textarea
            required
            data-testid="campaign-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="w-full bg-[var(--ivory)]/40 border border-black/10 p-3 font-mono text-xs outline-none focus:border-[#D4AF37]"
          />
        </FormField>

        <button
          type="submit"
          disabled={sending || recipientCount === 0}
          data-testid="campaign-send"
          className="w-full btn-gold h-12 tracking-[0.3em] text-[11px] uppercase font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Send size={14} strokeWidth={1.5} />
          {sending ? "Sending…" : recipientCount === 0 ? "No recipients in this segment" : `Send to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}`}
        </button>
      </form>

      <div className="bg-white border border-black/10 p-6">
        <h3 className="font-serif text-2xl mb-4">Past broadcasts</h3>
        {history.length === 0 ? (
          <div className="text-sm text-[#1A1A1A]/50 font-serif italic py-8 text-center">
            No campaigns sent yet.
          </div>
        ) : (
          <ul className="space-y-3">
            {history.map((c) => (
              <li key={c.id} className="border-b border-black/5 pb-3 last:border-0">
                <div className="flex justify-between items-baseline gap-3">
                  <div className="font-serif text-base truncate">{c.subject}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#1A1A1A]/50 shrink-0">
                    {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="text-xs mt-1 text-[#1A1A1A]/60">
                  {c.segment} · <span className="text-[#0B3B24]">{c.delivered} delivered</span>
                  {c.failed > 0 && <span className="text-[#722F37]"> · {c.failed} failed</span>}
                  <span className="text-[#1A1A1A]/40"> · {c.total_recipients} recipients</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
